<template>
  <article class="teaser-poster" :class="computedFluidStyle">
    <component
      :is="tag === 'button' ? 'button' : SmartLink"
      :to="tag !== 'button' ? generatedUrl || url : undefined"
      :tag="tag !== 'button' ? tag : undefined"
      :target="tag !== 'button' ? target : undefined"
      :rel="tag !== 'button' ? rel : undefined"
      :label="tag !== 'button' ? `${ariaLabelPrefix} ${teaserTitle} ${ariaLabelSuffix}` : undefined"
      class="inner"
      type="button"
      @click="handleClick"
    >
      <DynamicImage
        v-if="teaserImage"
        :renditions="computedRenditions"
        :image="teaserImage"
        class="poster-image"
        :loading="loading"
        :priority="priority"
        :fluid="fluid"
      >
        <div class="meta">
          <div class="badge-wrapper">
            <Badge
              v-if="video"
              icon="eenvandaag-general:play-solid"
              size="medium"
              :title="computedTitle"
            />
            <Badge v-if="external" icon="eenvandaag-general:external-2" size="medium" />
          </div>
          <DateTime
            v-if="showDate && !external"
            v-bind="props"
            class="date"
            :class="computedDateStyle"
          />
          <span v-if="computedShowLabel" class="label"> {{ label || teaserLabel }}</span>
          <span v-if="!showLabel && subTitle" class="sub-title">{{ subTitle }}</span>
          <Heading
            v-if="!ariaHidden"
            :level="headingLevel"
            :content="teaserTitle as string"
            class="heading"
            :class="headingSize"
            :max-lines="props.label || props.subTitle ? 3 : 6"
          />
        </div>
      </DynamicImage>
    </component>
  </article>
</template>

<script setup lang="ts">
import type {
  IAnalytics,
  ICollectionTeaser,
  IImageLoadingSettings,
  IImageRendition,
  TAspectRatio,
} from '~~/types/local_types';
import useArticleData from '~/composables/useArticleData';
import DateTime from '~/components/DateTime.vue';
import { SmartLink } from '#components';
import formatUtils from '~/utils/formatUtils';
const emit = defineEmits(['clicked']);

interface IProps extends IImageLoadingSettings, ICollectionTeaser {
  aspectRatio?: TAspectRatio;
  renditions?: IImageRendition[]; // when set, aspectRatio is ignored
  label?: string;
  showDate?: boolean;
  showLabel?: boolean;
  fluid?: boolean;
  headingLevel?: number;
  analytics?: IAnalytics;
  headingSize?: 'title-xl' | 'title-xs' | 'heading-md';
  datePosition?: 'top' | 'bottom';
  subTitle?: string;
  ariaLabelPrefix?: string;
  ariaLabelSuffix?: string;
  ariaHidden?: boolean;
  tag?: 'NuxtLink' | 'a' | 'button';
}

const props = withDefaults(defineProps<IProps>(), {
  slug: '',
  aspectRatio: '16x9',
  renditions: undefined,
  label: undefined,
  loading: 'lazy',
  priority: 'auto',
  showDate: false,
  showLabel: true,
  fluid: false,
  headingLevel: 3,
  analytics: undefined,
  headingSize: 'title-xl',
  datePosition: 'bottom',
  subTitle: '',
  ariaLabelPrefix: '',
  ariaLabelSuffix: '',
  ariaHidden: false,
  tag: 'NuxtLink',
});

const {
  image: teaserImage,
  title: teaserTitle,
  url: generatedUrl,
  target,
  rel,
  video,
  external,
  label: teaserLabel,
} = useArticleData(props);

const { formatIsoDurationNoZeros } = formatUtils();

const computedRenditions = computed(() => {
  // if manual renditions were set, use those.
  if (props.renditions) return props.renditions;

  return [
    {
      breakpoint: 'sm',
      size: 480,
      aspectRatio: (props.aspectRatio || '3x2') as TAspectRatio,
    },
    {
      breakpoint: 'lg',
      size: 640,
      aspectRatio: (props.aspectRatio || '3x2') as TAspectRatio,
    },
  ];
});

const computedFluidStyle = computed(() => {
  return props.fluid ? 'is--fluid' : '';
});

const computedDateStyle = computed(() => {
  return `is--${props.datePosition}`;
});

const computedShowLabel = computed(() => {
  return props.showLabel && (props.label || (teaserLabel && teaserLabel !== 'artikel'));
});

const handleClick = () => {
  emit('clicked', props);
  const analyticsDefaults = {
    linkText: teaserTitle,
    linkUrl: generatedUrl || props.url,
    linkAnalyticsTag: 'teaser',
  };
  gtmTrackEvent({ ...analyticsDefaults, ...props?.analytics });
};

const computedTitle = computed(() => {
  if (!props.mainEmbed || !props.mainEmbed[0]) return;
  return formatIsoDurationNoZeros(props.mainEmbed[0]?.contentEmbedsId?.duration);
});
</script>

<style scoped lang="postcss">
.teaser-poster {
  &.is--fluid {
    position: relative;
    &::before {
      content: '';
      display: block;
      padding-top: 320px;

      @media (--md-viewport) {
        padding-top: 66%; /* 66% of the element's width */
      }

      z-index: -1;
    }

    height: 100%;

    .inner {
      position: absolute;
      top: 0;
      left: 0;
    }
  }
}

.inner {
  /* unset default button classes (when button) */
  padding: 0;
  background-color: transparent;
  border: unset;
  text-align: left;
  cursor: pointer;

  overflow: hidden;
  display: block;
  width: 100%;
  height: 100%;

  @add-mixin hover {
    .poster-image:deep(.image) {
      scale: 1.05;
    }

    .heading {
      color: hsl(var(--color-primary-200));
    }
  }

  &:active {
    .heading {
      color: hsl(var(--color-primary-300));
    }
  }

  @add-mixin focus light {
  }
}

.poster-image:deep(.image) {
  transition: scale 250ms ease-in;
  transform-origin: center center;
}

.meta {
  --meta-padding: var(--unit-24) var(--unit-12) var(--unit-12) var(--unit-12);
  position: absolute;
  bottom: 0;
  left: 0;
  width: 100%;
  padding: var(--meta-padding);

  display: flex;
  flex-direction: column;
  gap: var(--unit-8);

  background: linear-gradient(
    0deg,
    hsla(var(--color-neutral-black), 0.6) 17.5%,
    hsla(var(--color-neutral-black), 0) 95%
  );

  @media (--lg-viewport) {
    --meta-padding: var(--unit-48) var(--unit-20) var(--unit-20) var(--unit-20);
  }

  > * {
    display: block;
  }

  .heading {
    color: hsl(var(--color-neutral-white));
    transition: color 250ms ease-in;

    /* this component has some variations in heading typography */
    &.title-xl {
      @mixin title-xl {
      }
    }

    &.title-xs {
      @mixin title-xs {
      }
    }

    &.heading-md {
      @mixin heading-md {
      }
    }
  }

  .label {
    color: hsl(var(--color-primary-200));
    font-family: var(--font-family-primary), sans-serif;
    @mixin label-md {
    }
  }

  .sub-title {
    color: hsl(var(--color-primary-750));
    display: flex;
    padding: var(--unit-6) var(--unit-12);
    flex-direction: column;
    justify-content: center;
    align-self: flex-start;

    border-radius: var(--border-radius-4);
    background: hsl(var(--color-neutral-white));

    @mixin label-md-semi-bold {
    }
    line-height: 100%;
  }

  .date {
    color: hsl(var(--color-primary-200));
    @mixin label-md {
    }

    &.is--bottom {
      order: 4;
    }
  }
}
</style>
